<template>
    <div class="rtb-requests">
        <div class="rtb-requests__header">
            <h2 class="rtb-requests__title">Журнал запросов</h2>
            <div class="rtb-requests__total">
                <div class="stat-card stat-card--total">
                    <div class="stat-card__label">Всего запросов</div>
                    <div class="stat-card__value">{{ stats.total || 0 }}</div>
                </div>
            </div>
            <div class="rtb-requests__stats">
                <div
                    v-for="opt in statusOptions"
                    :key="opt.value"
                    :class="['stat-card', `stat-card--${opt.value}`]"
                >
                    <div class="stat-card__label">{{ opt.label }}</div>
                    <div class="stat-card__value">{{ stats[opt.value] || 0 }}</div>
                </div>
            </div>
        </div>

        <!-- Фильтры -->
        <div class="rtb-requests__filters" :class="{ 'is-admin': isAdmin }">
            <div class="filter-group">
                <label class="filter-label">Статус:</label>
                <FilterMultiSelect
                    v-model="uiFilters.status"
                    :options="statusOptions"
                    all-label="Все статусы"
                />
            </div>
            <div class="filter-group">
                <label class="filter-label">SSP:</label>
                <FilterMultiSelect
                    v-model="uiFilters.ssp"
                    :options="sspSelectOptions"
                    all-label="Все SSP"
                />
            </div>
            <div v-if="isAdmin" class="filter-group">
                <label class="filter-label">OID:</label>
                <FilterMultiSelect
                    v-model="uiFilters.oid"
                    :options="oidSelectOptions"
                    all-label="Все OID"
                />
            </div>
            <div v-if="isAdmin" class="filter-group">
                <label class="filter-label">Frame GID:</label>
                <FilterMultiSelect
                    v-model="uiFilters.gid"
                    :options="gidSelectOptions"
                    all-label="Все GID"
                    searchable
                />
            </div>
            <div class="filter-datetime-stack">
                <div class="filter-group">
                    <label class="filter-label">С (KZ):</label>
                    <input v-model="uiFilters.start_date" type="datetime-local" step="1"
                        class="filter-input filter-input--datetime" />
                </div>
                <div class="filter-group">
                    <label class="filter-label">По (KZ):</label>
                    <input v-model="uiFilters.end_date" type="datetime-local" step="1"
                        class="filter-input filter-input--datetime" />
                </div>
            </div>
            <div class="filter-actions">
                <button @click="applyFilters" class="btn btn--primary btn--with-badge">
                    Применить фильтры
                    <span v-if="activeUiFilterCount > 0" class="filter-apply-badge">{{ activeUiFilterCount }}</span>
                </button>
                <button @click="resetFilters" class="btn btn--secondary">Сбросить</button>
                <button @click="exportToExcel" class="btn btn--secondary" :disabled="exporting">
                    {{ exporting ? 'Загрузка...' : 'Скачать в Excel' }}
                </button>
            </div>

        </div>

        <!-- ══════════ Таблица: АДМИН ══════════ -->
        <div class="rtb-requests__table-container">
            <table class="rtb-table" v-if="!loading && isAdmin">
                <thead>
                    <tr>
                        <th class="col-ids">Req ID / OID / Frame GID</th>
                        <th class="col-time">Время запроса</th>
                        <th class="col-default">Длительность</th>
                        <th class="col-default">Мин. цена / Цены показа</th>
                        <th class="col-default">Статус</th>
                        <th class="col-default">Результат</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="request in requests" :key="request.id">
                        <td class="vertical-cell col-ids">
                            <div v-if="request.ssp" class="request-meta">
                                <span
                                    :class="['ssp-badge', `ssp-badge--${String(request.ssp).toLowerCase()}`]">
                                    {{ sspLabel(request.ssp) }}
                                </span>
                            </div>
                            <div class="vertical-item">
                                <span class="vertical-label">Req ID:</span>
                                <span class="req-id">{{ request.req_id }}</span>
                            </div>
                            <div class="vertical-item">
                                <span class="vertical-label">OID:</span>
                                <div class="oid-block">
                                    <span v-if="request.organization_name" class="org-name">{{ request.organization_name
                                        }}</span>
                                    <span class="oid-value">{{ getOidDisplayValue(request) }}</span>
                                </div>
                            </div>
                            <div class="vertical-item">
                                <span class="vertical-label">GID:</span>
                                <div class="col-ids__value">
                                    <span :class="['gid-badge', getGidBadgeClass(request)]">
                                        <a v-if="request.gid_confirmed && request.gid"
                                            @click.prevent="openFrameMapModal(request.gid)" class="frame-gid-link"
                                            href="#">
                                            {{ request.gid }}
                                        </a>
                                        <template v-else-if="getGidDisplayValue(request)">{{
                                            getGidDisplayValue(request) }}</template>
                                        <template v-else>-</template>
                                    </span>
                                    <div v-if="request.frame_creative_size" class="frame-creative-size">
                                        {{ request.frame_creative_size }}
                                    </div>
                                    <div v-if="request.frame_address" class="frame-address"
                                        :title="request.frame_address">
                                        {{ request.frame_address }}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td class="vertical-cell col-time">
                            <div class="vertical-item">
                                <span class="vertical-label">Время показа:</span>
                                <div class="time-item">
                                    <strong>UTC (<small class="text-muted">{{ getOffsetString(request.predicted_at,
                                        'UTC')
                                            }}</small>):</strong>
                                    <span>{{ formatUTCFromTimestamp(request.predicted_at) }} </span>
                                </div>
                                <div class="time-item">
                                    <strong>KZ (<small class="text-muted">{{ getOffsetString(request.predicted_at,
                                        'Asia/Aqtau') }}</small>):</strong>
                                    <span>{{ formatInZone(request.predicted_at, 'Asia/Aqtau') }} </span>
                                </div>
                                <div class="time-item">
                                    <strong>Local (<small class="text-muted">{{
                                        getLocalOffsetString(request.predicted_at) }}</small>):</strong>
                                    <span>{{ formatInLocal(request.predicted_at) }} </span>
                                </div>
                            </div>
                        </td>
                        <td class="col-default">{{ formatDuration(request.duration) }}</td>
                        <td class="vertical-cell col-default">
                            <div class="vertical-item">
                                <span class="vertical-label">Мин. цена:</span>
                                <span>{{ formatPricePrecise(request.min_price) }}</span>
                            </div>
                            <template v-if="request.prices?.length">
                                <div
                                    v-for="price in request.prices"
                                    :key="price.key"
                                    class="vertical-item"
                                >
                                    <span class="vertical-label">{{ price.label }}:</span>
                                    <span>{{ formatPricePrecise(price.amount) }}</span>
                                </div>
                            </template>
                            <div v-else class="vertical-item">
                                <span class="vertical-label">SSP факт:</span>
                                <span>{{ request.winner_price ? formatPricePrecise(request.winner_price) : '-' }}</span>
                            </div>
                        </td>
                        <td class="col-default">
                            <span :class="['status-badge', `status-badge--${request.status}`]">
                                {{ getStatusLabel(request.status) }}
                            </span>
                        </td>
                        <td class="error-cell col-default">
                            <span v-if="request.error" class="error-icon"
                                @click.prevent="openErrorModal(request.error)">
                                ⚠️
                            </span>
                            <template v-else-if="request.status === 'completed'">
                                <div class="creative-link-row">
                                    <template v-if="request.creative_id">
                                        <a @click.prevent="openCreativeModal(request.creative_id, request.creative)"
                                            class="link-button" href="#"
                                            :title="getCreativeTooltip(request.creative)">
                                            Креатив: {{ request.creative_id }}
                                        </a>
                                        <span v-if="formatCreativeLocale(request.locale)" class="creative-locale-badge">
                                            {{ formatCreativeLocale(request.locale) }}
                                        </span>
                                    </template>
                                    <span v-else class="text-muted">-</span>
                                </div>
                                <div v-if="request.campaign" style="margin-top:6px;">
                                    <a href="#" class="link-button" @click.prevent="goToCampaign(request.campaign)">
                                        Кампания: {{ request.campaign.id }}
                                    </a>
                                </div>
                            </template>
                            <span v-else class="text-muted">-</span>
                        </td>
                    </tr>
                    <tr v-if="requests.length === 0 && !loading">
                        <td colspan="6" class="text-center text-muted">
                            Запросы не найдены
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- ══════════ Таблица: ПОЛЬЗОВАТЕЛЬ ══════════ -->
            <table class="rtb-table" v-if="!loading && !isAdmin">
                <thead>
                    <tr>
                        <th class="col-ids">Кампания / Экран</th>
                        <th class="col-time">Время</th>
                        <th class="col-default">Длительность</th>
                        <th class="col-default">Цены</th>
                        <th class="col-default">Статус</th>
                        <th class="col-default">Креатив</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="request in requests" :key="request.id">
                        <td class="vertical-cell col-ids">
                            <div v-if="request.ssp" class="request-meta">
                                <span :class="['ssp-badge', `ssp-badge--${String(request.ssp).toLowerCase()}`]">
                                    {{ sspLabel(request.ssp) }}
                                </span>
                            </div>
                            <div class="vertical-item">
                                <span class="vertical-label">Кампания:</span>
                                <span>{{ request.campaign_name || '-' }}</span>
                            </div>
                            <div class="vertical-item">
                                <span class="vertical-label">Экран:</span>
                                <div class="col-ids__value">
                                    <span v-if="getGidDisplayValue(request)"
                                        :class="['gid-badge', getGidBadgeClass(request)]">
                                        {{ getGidDisplayValue(request) }}
                                    </span>
                                    <span v-else class="frame-address"
                                        :title="request.frame_address || ''">{{
                                            request.frame_address || '-' }}</span>
                                    <div v-if="request.frame_creative_size" class="frame-creative-size">
                                        {{ request.frame_creative_size }}
                                    </div>
                                    <div v-if="getGidDisplayValue(request) && request.frame_address"
                                        class="frame-address" :title="request.frame_address">
                                        {{ request.frame_address }}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td class="col-time">
                            <div>
                                <strong>UTC:</strong>
                                <span>{{ formatUTCFromTimestamp(request.predicted_at) }} (<small class="text-muted">{{
                                    getOffsetString(request.predicted_at, 'UTC') }}</small>)</span>
                            </div>
                            <div>
                                <strong>KZ:</strong>
                                <span>{{ formatInZone(request.predicted_at, 'Asia/Aqtau') }} (<small
                                        class="text-muted">{{
                                            getOffsetString(request.predicted_at, 'Asia/Aqtau') }}</small>)</span>
                            </div>
                            <div>
                                <strong>Local:</strong>
                                <span>{{ formatInLocal(request.predicted_at) }} (<small class="text-muted">{{
                                    getLocalOffsetString(request.predicted_at) }}</small>)</span>
                            </div>
                        </td>
                        <td class="col-default">{{ formatDuration(request.duration) }}</td>
                        <td class="vertical-cell col-default">
                            <template v-if="request.prices?.length">
                                <div
                                    v-for="price in request.prices"
                                    :key="price.key"
                                    class="vertical-item"
                                >
                                    <span class="vertical-label">{{ price.label }}:</span>
                                    <span>{{ formatPricePrecise(price.amount) }}</span>
                                </div>
                            </template>
                            <span v-else>{{ request.price ? formatPricePrecise(request.price) : '-' }}</span>
                        </td>
                        <td class="col-default">
                            <span :class="['status-badge', `status-badge--${request.status}`]">
                                {{ getStatusLabel(request.status) }}
                            </span>
                        </td>
                        <td class="error-cell col-default">
                            <span v-if="request.error" class="error-icon"
                                @click.prevent="openErrorModal(request.error)">
                                ⚠️
                            </span>
                            <template v-else-if="request.status === 'completed'">
                                <div class="creative-link-row">
                                    <template v-if="request.creative_id">
                                        <a @click.prevent="openCreativeModal(request.creative_id, request.creative)"
                                            class="link-button" href="#"
                                            :title="getCreativeTooltip(request.creative)">
                                            {{ request.creative?.name || `ID: ${request.creative_id}` }}
                                        </a>
                                        <span v-if="formatCreativeLocale(request.locale)" class="creative-locale-badge">
                                            {{ formatCreativeLocale(request.locale) }}
                                        </span>
                                    </template>
                                    <span v-else class="text-muted">-</span>
                                </div>
                                <div v-if="request.campaign" style="margin-top:6px;">
                                    <small>Кампания:
                                        <a href="#" class="link-button" @click.prevent="goToCampaign(request.campaign)">
                                            {{ request.campaign.id }} — {{ request.campaign.name || '-' }}
                                        </a>
                                    </small>
                                </div>
                            </template>
                            <span v-else class="text-muted">-</span>
                        </td>
                    </tr>
                    <tr v-if="requests.length === 0 && !loading">
                        <td colspan="6" class="text-center text-muted">
                            Запросы не найдены
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-else-if="loading && requests.length === 0" class="loading">Загрузка...</div>

            <!-- Кнопка загрузки еще -->
            <div v-if="hasMore && !loading && !loadingMore" class="load-more-container">
                <button @click="loadMore" class="btn btn--primary">
                    Загрузить еще 50
                </button>
            </div>
            <div v-if="loadingMore" class="loading-more">
                Загрузка...
            </div>
            <div v-if="!hasMore && requests.length > 0 && !loading" class="no-more">
                Все записи загружены
            </div>
        </div>

        <!-- Модальное окно для просмотра снимка -->
        <div v-if="showScreenshotModal" class="modal-overlay" @click="closeScreenshotModal">
            <div class="modal-content screenshot-modal" @click.stop>
                <div class="modal-header">
                    <h3>Снимок показа</h3>
                    <button class="modal-close" @click="closeScreenshotModal">&times;</button>
                </div>
                <div class="modal-body screenshot-modal-body">
                    <div v-if="!currentScreenshotUrl && !imageError" class="screenshot-loading">
                        Загрузка снимка...
                    </div>
                    <img v-if="currentScreenshotUrl" :src="currentScreenshotUrl" alt="Screenshot"
                        @error="handleImageError" class="screenshot-image" />
                    <div v-if="imageError" class="screenshot-error">
                        {{ typeof imageError === 'string' ? imageError : 'Не удалось загрузить изображение' }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Модальное окно для просмотра фрейма на карте -->
        <div v-if="showFrameMapModal" class="modal-overlay" @click="closeFrameMapModal">
            <div class="modal-content frame-map-modal" @click.stop>
                <div class="modal-header">
                    <h3>Расположение экрана {{ currentFrame?.gid ? `#${currentFrame.gid}` : '' }}</h3>
                    <button class="modal-close" @click="closeFrameMapModal">&times;</button>
                </div>
                <div class="modal-body frame-map-modal-body">
                    <div v-if="loadingFrame" class="frame-loading">
                        Загрузка данных экрана...
                    </div>
                    <div v-else-if="frameError" class="frame-error">
                        {{ frameError }}
                    </div>
                    <div v-else-if="currentFrame && currentFrame.lat && currentFrame.lon" class="frame-map-container">
                        <div class="frame-info">
                            <p v-if="currentFrame.address" class="frame-address">
                                <strong>Адрес:</strong> {{ currentFrame.address }}
                            </p>
                            <p v-if="currentFrame.city" class="frame-city">
                                <strong>Город:</strong> {{ currentFrame.city }}
                            </p>
                            <p v-if="currentFrame.width && currentFrame.height" class="frame-size">
                                <strong>Размер креатива:</strong> {{ currentFrame.width }}×{{ currentFrame.height }}
                            </p>
                            <p v-if="currentFrame.physical_size_width && currentFrame.physical_size_height"
                                class="frame-physical-size">
                                <strong>Физический размер:</strong> {{ currentFrame.physical_size_width }}×{{
                                    currentFrame.physical_size_height }}
                            </p>
                        </div>
                        <GMapMap :center="{ lat: Number(currentFrame.lat), lng: Number(currentFrame.lon) }" :zoom="15"
                            style="width: 100%; height: 500px;">
                            <GMapMarker :position="{ lat: Number(currentFrame.lat), lng: Number(currentFrame.lon) }"
                                :clickable="true">
                                <GMapInfoWindow :opened="true" :options="{ disableAutoPan: false }">
                                    <div style="width: 300px">
                                        <p style="margin: 0; font-weight: 500">
                                            {{ currentFrame.address || currentFrame.city || `Экран #${currentFrame.gid}`
                                            }}
                                        </p>
                                        <p v-if="currentFrame.city" style="margin: 0; font-size: 12px; color: #555">
                                            <span v-if="currentFrame.city">{{ currentFrame.city }}</span>
                                        </p>
                                        <p v-if="currentFrame.width && currentFrame.height"
                                            style="margin: 0; font-size: 12px; color: #555">
                                            Размер креатива: {{ currentFrame.width }}×{{ currentFrame.height }}
                                        </p>
                                    </div>
                                </GMapInfoWindow>
                            </GMapMarker>
                        </GMapMap>
                    </div>
                    <div v-else-if="currentFrame" class="frame-no-coords">
                        Координаты экрана не указаны
                    </div>
                </div>
            </div>
        </div>

        <!-- Модальное окно для просмотра креатива -->
        <div v-if="showCreativeModal" class="modal-overlay" @click="closeCreativeModal">
            <div class="modal-content creative-modal" @click.stop>
                <div class="modal-header">
                    <h3>Предпросмотр креатива {{ currentCreative?.id ? `#${currentCreative.id}` : '' }}</h3>
                    <button class="modal-close" @click="closeCreativeModal">&times;</button>
                </div>
                <div class="modal-body creative-modal-body">
                    <div v-if="loadingCreative" class="creative-loading">
                        Загрузка креатива...
                    </div>
                    <div v-else-if="creativeError" class="creative-error">
                        {{ creativeError }}
                    </div>
                    <div v-else-if="currentCreative" class="creative-preview">
                        <div v-if="currentCreative.name" class="creative-name">
                            {{ currentCreative.name }}
                        </div>
                        <div v-if="isImageCreative(currentCreative)" class="creative-image-container">
                            <img :src="getCreativeUrl(currentCreative)" :alt="currentCreative.name || 'Creative'"
                                @error="handleCreativeImageError" class="creative-image" />
                        </div>
                        <div v-else-if="isVideoCreative(currentCreative)" class="creative-video-container">
                            <video :src="getCreativeUrl(currentCreative)" controls class="creative-video">
                                Ваш браузер не поддерживает видео.
                            </video>
                        </div>
                        <div v-else class="creative-info-text">
                            <p>Тип: {{ currentCreative.type || 'Неизвестно' }}</p>
                            <p v-if="currentCreative.url">URL: <a :href="currentCreative.url" target="_blank">{{
                                currentCreative.url }}</a></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Модальное окно для отображения ошибок запроса -->
        <div v-if="showErrorModal" class="modal-overlay" @click="closeErrorModal">
            <div class="modal-content creative-modal error-rejection-modal" @click.stop>
                <div class="modal-header">
                    <h3>Причины отказа<span v-if="errorModalItems.length" class="error-rejection-count"> ({{ errorModalItems.length }})</span></h3>
                    <button class="modal-close" @click="closeErrorModal">&times;</button>
                </div>
                <div class="modal-body creative-modal-body">
                    <ul v-if="errorModalItems.length" class="error-rejection-list">
                        <li v-for="(item, idx) in errorModalItems" :key="idx" class="error-rejection-item"
                            :title="item.priority != null ? `Приоритет: ${item.priority}` : undefined">
                            <div class="error-rejection-item__main">
                                <span class="error-rejection-item__num">{{ idx + 1 }}</span>
                                <div class="error-rejection-item__content">
                                    <div v-if="item.code" class="error-rejection-code">
                                        <span class="error-rejection-code__badge">{{ item.code }}</span>
                                        <span class="error-rejection-code__label">{{ getRejectionCodeLabel(item.code) }}</span>
                                    </div>
                                    <p class="error-rejection-text">
                                        <span v-if="containsHtml(item.text)" v-html="item.text"></span>
                                        <span v-else>{{ item.text }}</span>
                                    </p>
                                </div>
                            </div>
                            <div class="error-rejection-actions">
                                <a v-if="resolveCampaignId(item)" href="#" class="link-button error-rejection-link"
                                    @click.prevent="goToCampaign({ id: resolveCampaignId(item) })">
                                    Кампания #{{ resolveCampaignId(item) }}
                                </a>
                                <button v-else-if="item.campaign && item.campaign.id" type="button"
                                    class="btn btn--small" @click.prevent="goToCampaign(item.campaign)">
                                    Перейти к кампании
                                </button>
                            </div>
                        </li>
                    </ul>
                    <p v-else class="error-rejection-empty">Нет данных об отказе</p>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import axios from 'axios';
import {
    Map as GMapMap,
    Marker as GMapMarker,
    InfoWindow as GMapInfoWindow,
} from '@fawmi/vue-google-maps';
import {
    formatInRtbTZ,
    formatTimestampMsInRtbTZ,
    getDefaultRtbFilterRange,
    rtbFilterToApiDatetime,
} from '../../utils/timezones';
import { parseErrorPayload, rejectionCodeLabel } from '../../utils/rtbRejectionErrors';
import { useToast } from '../../composables/useToast';
import { notifyHttpError } from '../../utils/httpErrors';
import FilterMultiSelect from '../common/FilterMultiSelect.vue';

export default {
    name: 'RTBRequests',
    components: {
        GMapMap,
        GMapMarker,
        GMapInfoWindow,
        FilterMultiSelect,
    },
    props: {
        userData: {
            type: Object,
            required: true,
        },
    },
    data() {
        const defaultRange = getDefaultRtbFilterRange();

        return {
            // Single source of truth for filter + badge labels (logical filter order)
            statusOptions: [
                { value: 'completed', label: 'Успешные' },
                { value: 'bid_sent', label: 'Ставка отправлена' },
                { value: 'pending', label: 'Ожидают' },
                { value: 'no_bid', label: 'Без ставки' },
                { value: 'failed', label: 'Неудачные' },
            ],
            requests: [],
            stats: {
                total: 0,
                completed: 0,
                bid_sent: 0,
                pending: 0,
                no_bid: 0,
                failed: 0,
            },
            // Applied filters used for API requests
            filters: {
                status: [],
                ssp: [],
                oid: [],
                gid: [],
                start_date: defaultRange.start_date,
                end_date: defaultRange.end_date,
            },
            // UI filters (editable) applied only after pressing "Применить фильтры"
            uiFilters: {
                status: [],
                ssp: [],
                oid: [],
                gid: [],
                start_date: defaultRange.start_date,
                end_date: defaultRange.end_date,
            },
            oidOptions: [],
            gidOptions: [],
            sspOptions: [],
            filterOptionsRequestKey: null,
            filterOptionsRequestId: 0,
            loading: false,
            loadingMore: false,
            exporting: false,
            hasMore: false,
            showScreenshotModal: false,
            currentScreenshotUrl: null,
            imageError: false,
            showCreativeModal: false,
            currentCreative: null,
            loadingCreative: false,
            creativeError: null,
            showFrameMapModal: false,
            currentFrame: null,
            loadingFrame: false,
            frameError: null,
            // Error modal state
            showErrorModal: false,
            errorModalItems: [],
            errorModalKind: 'plain',
        };
    },
    computed: {
        isAdmin() {
            return this.userData?.is_admin === true;
        },
        activeUiFilterCount() {
            return ['status', 'ssp', 'oid', 'gid']
                .filter((key) => (this.uiFilters[key] || []).length > 0)
                .length;
        },
        sspSelectOptions() {
            return this.sspOptions.map((ssp) => ({
                value: ssp,
                label: this.sspLabel(ssp),
            }));
        },
        oidSelectOptions() {
            const facet = this.oidOptions.map((option) => ({
                value: String(option.oid),
                label: option.name || String(option.oid),
            }));

            return this.mergeSelectedOptions(facet, this.uiFilters.oid, (value) => {
                const known = this.oidOptions.find((option) => String(option.oid) === value);
                return known?.name || value;
            });
        },
        gidSelectOptions() {
            const selectedOids = new Set((this.uiFilters.oid || []).map((value) => String(value)));
            const source = selectedOids.size === 0
                ? this.gidOptions
                : this.gidOptions.filter((option) => selectedOids.has(String(option.oid ?? '')));
            const facet = source.map((option) => ({
                value: String(option.gid),
                label: this.gidOptionLabel(option),
            }));

            return this.mergeSelectedOptions(facet, this.uiFilters.gid, (value) => {
                const known = this.gidOptions.find((option) => String(option.gid) === value);
                return known ? this.gidOptionLabel(known) : value;
            });
        },
    },
    mounted() {
        this.loadFilterOptions();
        this.loadRequests();
    },
    watch: {
        'uiFilters.ssp': {
            deep: true,
            handler(ssps) {
                this.loadFilterOptions(ssps);
            },
        },
    },
    methods: {
        mergeSelectedOptions(options, selected, labelOf) {
            const present = new Set(options.map((option) => String(option.value)));
            const extras = (selected || [])
                .map((value) => String(value))
                .filter((value) => value !== '' && !present.has(value))
                .map((value) => ({ value, label: labelOf(value) }));

            return extras.length === 0 ? options : [...options, ...extras];
        },
        gidOptionLabel(option) {
            const gid = String(option?.gid ?? '');
            const address = String(option?.label ?? '').trim();
            if (address === '' || address === gid) {
                return gid;
            }

            return `${gid} — ${address}`;
        },
        copyListFilters(source) {
            return {
                status: [...(source.status || [])],
                ssp: [...(source.ssp || [])],
                oid: [...(source.oid || [])],
                gid: [...(source.gid || [])],
                start_date: source.start_date,
                end_date: source.end_date,
            };
        },
        buildApiParams(extra = {}) {
            const params = { ...extra };
            ['status', 'ssp', 'oid', 'gid'].forEach((key) => {
                const values = this.filters[key] || [];
                if (values.length > 0) {
                    params[key] = [...values];
                }
            });
            if (this.filters.start_date) {
                params.start_date = rtbFilterToApiDatetime(this.filters.start_date);
            }
            if (this.filters.end_date) {
                params.end_date = rtbFilterToApiDatetime(this.filters.end_date);
            }
            return params;
        },
        hasFilterPeriod(source) {
            const period = source || this.filters;
            return Boolean(period.start_date && period.end_date);
        },
        async loadRequests() {
            if (!this.hasFilterPeriod()) {
                useToast().error('Укажите начало и конец периода');
                return;
            }

            this.loading = true;
            try {
                const params = this.buildApiParams();

                const response = await axios.get('/api/rtb-requests', { params });

                this.requests = response.data.requests;
                this.stats = response.data.stats;
                this.hasMore = response.data.has_more || false;
            } catch (error) {
                notifyHttpError(error, 'Не удалось загрузить запросы');
            } finally {
                this.loading = false;
            }
        },
        async loadMore() {
            if (this.loadingMore || !this.hasMore || !this.hasFilterPeriod()) {
                return;
            }

            const beforeId = this.requests.length
                ? this.requests[this.requests.length - 1].id
                : null;
            if (!beforeId) {
                return;
            }

            this.loadingMore = true;
            try {
                const params = this.buildApiParams({ before_id: beforeId });

                const response = await axios.get('/api/rtb-requests', { params });

                this.requests = [...this.requests, ...response.data.requests];
                this.hasMore = response.data.has_more || false;
            } catch (error) {
                notifyHttpError(error, 'Не удалось загрузить запросы');
            } finally {
                this.loadingMore = false;
            }
        },
        resetFilters() {
            const defaultRange = getDefaultRtbFilterRange();
            this.uiFilters = {
                status: [],
                ssp: [],
                oid: [],
                gid: [],
                start_date: defaultRange.start_date,
                end_date: defaultRange.end_date,
            };
        },
        applyFilters() {
            if (!this.hasFilterPeriod(this.uiFilters)) {
                useToast().error('Укажите начало и конец периода');
                return;
            }
            this.filters = this.copyListFilters(this.uiFilters);
            this.loadRequests();
        },
        async exportToExcel() {
            if (this.exporting) {
                return;
            }
            if (!this.hasFilterPeriod()) {
                useToast().error('Укажите начало и конец периода');
                return;
            }

            this.exporting = true;
            try {
                const queryParams = new URLSearchParams();
                const apiParams = this.buildApiParams();
                Object.entries(apiParams).forEach(([key, value]) => {
                    if (Array.isArray(value)) {
                        value.forEach((item) => {
                            if (item !== '' && item != null) {
                                queryParams.append(`${key}[]`, String(item));
                            }
                        });
                        return;
                    }
                    if (value !== '' && value != null) {
                        queryParams.append(key, String(value));
                    }
                });

                const response = await fetch(`/api/rtb-requests/export?${queryParams.toString()}`, {
                    method: 'GET',
                    headers: {
                        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    credentials: 'same-origin',
                });

                if (!response.ok) {
                    throw new Error('Ошибка при экспорте данных');
                }

                const contentDisposition = response.headers.get('Content-Disposition');
                let filename = 'rtb-requests.xlsx';
                if (contentDisposition) {
                    let filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/i);
                    if (filenameMatch) {
                        filename = decodeURIComponent(filenameMatch[1]);
                    } else {
                        filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
                        if (filenameMatch) {
                            filename = filenameMatch[1];
                        }
                    }
                }

                if (!filename.endsWith('.xlsx')) {
                    filename = filename.replace(/\.xlsx_?$/, '') + '.xlsx';
                }

                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
            } catch (error) {
                useToast().error('Не удалось выгрузить данные в Excel');
            } finally {
                this.exporting = false;
            }
        },
        async loadFilterOptions(selectedSsps = this.uiFilters?.ssp || []) {
            const ssps = (Array.isArray(selectedSsps) ? selectedSsps : [])
                .map((value) => String(value).trim())
                .filter(Boolean);
            const key = [...ssps].sort().join('\0');
            if (key === this.filterOptionsRequestKey) {
                return;
            }
            const requestId = (this.filterOptionsRequestId || 0) + 1;
            this.filterOptionsRequestId = requestId;

            try {
                const params = {};
                if (ssps.length > 0) {
                    params.ssp = ssps;
                }

                const res = await axios.get('/api/rtb-requests/filters', { params });
                if (this.filterOptionsRequestId !== requestId) {
                    return;
                }
                this.filterOptionsRequestKey = key;
                this.oidOptions = res.data.oids || [];
                this.gidOptions = res.data.gids || [];
                this.sspOptions = res.data.ssps || [];
            } catch (e) {
                if (this.filterOptionsRequestId !== requestId) {
                    return;
                }
                this.oidOptions = [];
                this.gidOptions = [];
                this.sspOptions = [];
            }
        },
        getStatusLabel(status) {
            return this.statusOptions.find((opt) => opt.value === status)?.label || status;
        },
        sspLabel(ssp) {
            const labels = {
                cityscreen: 'CityScreen',
                spinetix: 'Spinetix',
                nowmedia: 'Spinetix',
                ledex: 'LEDEX',
                citix: 'Citix',
            };
            const key = String(ssp || '').toLowerCase();
            return labels[key] || ssp;
        },
        getGidBadgeClass(request) {
            const hasGid = Boolean(request?.gid || request?.incoming_gid);
            if (!hasGid) {
                return 'gid-badge--neutral';
            }
            return request?.gid_confirmed ? 'gid-badge--confirmed' : 'gid-badge--unconfirmed';
        },
        getGidDisplayValue(request) {
            const gid = request?.gid || request?.incoming_gid;
            if (!gid || String(gid).trim() === '') {
                return '';
            }
            return String(gid);
        },
        hasOidValue(request) {
            const oid = request?.oid;
            return Boolean(oid && String(oid).trim() !== '');
        },
        isSpinetixWithoutOid(request) {
            const ssp = String(request?.ssp || '').toLowerCase();
            return (ssp === 'spinetix' || ssp === 'nowmedia') && !this.hasOidValue(request);
        },
        getOidDisplayValue(request) {
            if (this.isSpinetixWithoutOid(request)) {
                return '-';
            }

            if (this.hasOidValue(request)) {
                return request.oid;
            }

            return '-';
        },
        formatDate(dateString) {
            return formatInRtbTZ(dateString);
        },
        formatLocal(dateIso) {
            if (!dateIso) return '—';
            try {
                const d = new Date(dateIso);
                if (isNaN(d.getTime())) return '—';
                return d.toLocaleString();
            } catch (e) {
                return '—';
            }
        },
        // Expose imported formatter to template (wrapper) so template expressions can call it.
        formatInRtbTZ(dateInput) {
            return formatInRtbTZ(dateInput);
        },
        formatPrice(price) {
            if (!price) return '-';
            return new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: 'KZT',
                currencyDisplay: 'symbol',
                minimumFractionDigits: 2,
            }).format(price);
        },
        formatPricePrecise(price) {
            if (!price) return '-';
            // Преобразуем в число для точного форматирования
            const numPrice = typeof price === 'string' ? parseFloat(price) : price;
            return new Intl.NumberFormat('kk-KZ', {
                style: 'currency',
                currency: 'KZT',
                currencyDisplay: 'symbol',
                minimumFractionDigits: 4,
                maximumFractionDigits: 4,
            }).format(numPrice);
        },
        formatPredictedTime(timestampMs) {
            return formatTimestampMsInRtbTZ(timestampMs);
        },
        // Parse predicted_at ISO (UTC) into ms
        parseIsoToMs(iso) {
            if (!iso) return null;
            const ms = Date.parse(iso);
            return isNaN(ms) ? null : ms;
        },

        // Helper: zero-pad
        pad(n) { return n.toString().padStart(2, '0'); },

        // Canonical formatter: format ISO in given IANA timezone using Intl.
        // Returns "DD.MM.YYYY, HH:MM:SS" or '—' when input invalid.
        formatInTimezone(iso, timeZone, locale = 'ru-RU') {
            const ms = this.parseIsoToMs(iso);
            if (ms === null) return '—';
            try {
                const opts = {
                    timeZone,
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit', second: '2-digit',
                    hour12: false,
                };
                // Use formatToParts to reliably build "DD.MM.YYYY, HH:MM:SS"
                const parts = new Intl.DateTimeFormat(locale, opts).formatToParts(new Date(ms));
                const map = {};
                parts.forEach(p => { if (p.type && p.value) map[p.type] = p.value; });
                return `${map.day}.${map.month}.${map.year}, ${map.hour}:${map.minute}:${map.second}`;
            } catch (e) {
                return '—';
            }
        },

        // Wrappers used by template
        formatUTCFromTimestamp(iso) {
            return this.formatInTimezone(iso, 'UTC', 'en-GB');
        },
        formatInZone(iso, timeZone) {
            return this.formatInTimezone(iso, timeZone, 'ru-RU');
        },
        formatInLocal(iso) {
            // Local formatting uses runtime locale and local timezone
            const ms = this.parseIsoToMs(iso);
            if (ms === null) return '—';
            try {
                const parts = new Intl.DateTimeFormat(undefined, {
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit', second: '2-digit',
                    hour12: false,
                }).formatToParts(new Date(ms));
                const map = {};
                parts.forEach(p => { if (p.type && p.value) map[p.type] = p.value; });
                return `${map.day}.${map.month}.${map.year}, ${map.hour}:${map.minute}:${map.second}`;
            } catch (e) {
                return '—';
            }
        },

        // Compute timezone offset string like +5 or +5:30 for given timezone at predicted_at moment.
        // Uses Intl to get localized parts and compares to UTC ms.
        getOffsetString(iso, timeZone) {
            const ms = this.parseIsoToMs(iso);
            if (ms === null) return '';
            if (timeZone === 'UTC') return '+0';
            try {
                const parts = new Intl.DateTimeFormat('en-GB', {
                    timeZone,
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit', second: '2-digit',
                    hour12: false,
                }).formatToParts(new Date(ms));
                const map = {};
                parts.forEach(p => { if (p.type && p.value) map[p.type] = p.value; });
                const tzMillis = Date.UTC(
                    Number(map.year),
                    Number(map.month) - 1,
                    Number(map.day),
                    Number(map.hour),
                    Number(map.minute),
                    Number(map.second)
                );
                const offsetMinutes = Math.round((tzMillis - ms) / 60000);
                const sign = offsetMinutes >= 0 ? '+' : '-';
                const abs = Math.abs(offsetMinutes);
                const hours = Math.floor(abs / 60);
                const mins = abs % 60;
                return `${sign}${hours}${mins ? `:${this.pad(mins)}` : ''}`;
            } catch (e) {
                return '';
            }
        },

        getLocalOffsetString(iso) {
            const ms = this.parseIsoToMs(iso);
            if (ms === null) return '';
            const d = new Date(ms);
            const offsetMin = -d.getTimezoneOffset();
            const sign = offsetMin >= 0 ? '+' : '-';
            const abs = Math.abs(offsetMin);
            const hours = Math.floor(abs / 60);
            const mins = abs % 60;
            return `${sign}${hours}${mins ? `:${this.pad(mins)}` : ''}`;
        },
        formatDuration(ms) {
            if (!ms) return '-';
            const seconds = Math.floor(ms / 1000);
            return `${seconds} сек`;
        },
        isScreenshotValid(expiresAt) {
            if (!expiresAt) return false;
            return new Date(expiresAt) > new Date();
        },
        truncateError(error) {
            if (!error) return '';
            if (error.length <= 50) return error;
            return error.substring(0, 50) + '...';
        },
        formatErrorTooltip(error) {
            if (!error) return '';

            const { kind, items } = parseErrorPayload(error);
            if ((kind === 'structured' || kind === 'legacy') && items.length > 0) {
                let html = '<div style="max-width:420px;text-align:left;">';
                items.forEach((item, idx) => {
                    const codePart = item.code
                        ? `<span style="font-size:11px;color:#6b7280;">${this.escapeHtml(item.code)}</span><br>`
                        : '';
                    html += `<div style="margin-bottom:8px;"><strong>${idx + 1}.</strong> ${codePart}${this.escapeHtml(item.text)}</div>`;
                });
                html += '</div>';
                return html;
            }

            const escaped = String(error)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/\n/g, '<br>');
            return `<div style="white-space: pre-line; max-width: 400px; text-align:left;">${escaped}</div>`;
        },

        // Helper to escape HTML for individual pieces
        escapeHtml(text) {
            if (text === null || text === undefined) return '';
            return String(text)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
        },

        // Returns tippy options object that shows tooltip on click and keeps it open
        // until user clicks outside.
        tippyOptions(error) {
            return {
                content: this.formatErrorTooltip(error),
                theme: 'light',
                placement: 'top',
                allowHTML: true,
                trigger: 'click',
                interactive: true,
                hideOnClick: false,
                // onClickOutside is supported by tippy; hide tooltip when clicking outside
                onClickOutside(instance) {
                    try {
                        instance.hide();
                    } catch (e) {
                        // ignore
                    }
                },
            };
        },
        // Open custom modal showing detailed error (used when tooltip doesn't work)
        openErrorModal(error) {
            const { kind, items } = parseErrorPayload(error);
            this.errorModalKind = kind;
            this.errorModalItems = items;
            this.showErrorModal = true;
        },

        closeErrorModal() {
            this.showErrorModal = false;
            this.errorModalItems = [];
            this.errorModalKind = 'plain';
        },

        getRejectionCodeLabel(code) {
            return rejectionCodeLabel(code);
        },

        resolveCampaignId(item) {
            if (!item) return null;
            if (item.campaign_id) return item.campaign_id;
            if (item.campaign && item.campaign.id) return item.campaign.id;
            return null;
        },
        containsHtml(text) {
            if (typeof text !== 'string') return false;
            const decoded = this.decodeHtml(text);
            return /<a\s|<\/a>/i.test(decoded);
        },
        decodeHtml(str) {
            if (typeof str !== 'string') return str;
            const txt = document.createElement('textarea');
            txt.innerHTML = str;
            return txt.value;
        },
        goToCampaign(campaign) {
            if (!campaign) return;
            // RTB campaigns — open RTB campaign edit page in a new tab
            const id = campaign.id || campaign.campaign_id;
            if (id) {
                const url = `/rtb/campaigns/${id}/edit`;
                try {
                    window.open(url, '_blank', 'noopener');
                } catch (e) {
                    window.location.href = url;
                }
            } else {
                useToast().error('Не удалось определить кампанию для перехода');
            }
        },
        async openScreenshotModal(reqId) {
            this.showScreenshotModal = true;
            this.imageError = false;
            this.currentScreenshotUrl = null; // Сбрасываем перед загрузкой

            try {
                // Вызываем наш API для получения снимка через прокси к SSP
                const response = await axios.get(`/api/impression-shot/${reqId}`, {
                    responseType: 'blob', // Получаем как blob для изображения
                });

                // Создаем URL из blob для отображения
                const blob = new Blob([response.data], { type: response.headers['content-type'] });
                this.currentScreenshotUrl = URL.createObjectURL(blob);
            } catch (error) {
                this.imageError = true;
                if (error.response?.status === 404) {
                    this.imageError = 'Снимки отсутствуют';
                } else {
                    this.imageError = 'Не удалось загрузить снимок';
                }
            }
        },
        closeScreenshotModal() {
            // Освобождаем память от blob URL
            if (this.currentScreenshotUrl && this.currentScreenshotUrl.startsWith('blob:')) {
                URL.revokeObjectURL(this.currentScreenshotUrl);
            }
            this.showScreenshotModal = false;
            this.currentScreenshotUrl = null;
            this.imageError = false;
        },
        handleImageError() {
            this.imageError = true;
        },
        getCreativeTooltip(creative) {
            if (!creative) return '';
            const parts = [];
            if (creative.name) parts.push(`Название: ${creative.name}`);
            if (creative.id) parts.push(`ID: ${creative.id}`);
            if (creative.type) parts.push(`Тип: ${creative.type}`);
            const localeLabel = this.formatCreativeLocale(creative.locale);
            if (localeLabel) parts.push(`Язык: ${localeLabel}`);
            if (creative.duration) parts.push(`Длительность: ${Math.floor(creative.duration / 1000)} сек`);
            if (creative.width && creative.height) parts.push(`Размер: ${creative.width}x${creative.height}`);
            if (creative.url) parts.push(`URL: ${creative.url}`);
            return parts.length > 0 ? parts.join('\n') : 'Креатив, который был показан';
        },
        formatCreativeLocale(locale) {
            if (locale === 'ru') return 'RU';
            if (locale === 'kz') return 'KZ';
            return null;
        },
        async openCreativeModal(creativeId, creativeData) {
            this.showCreativeModal = true;
            this.loadingCreative = true;
            this.creativeError = null;
            this.currentCreative = null;

            // Если данные креатива уже есть в запросе, используем их
            if (creativeData && creativeData.url) {
                this.currentCreative = creativeData;
                this.loadingCreative = false;
                return;
            }

            // Если данных нет, показываем сообщение
            this.creativeError = 'Данные креатива недоступны. Креатив был показан, но информация о нем не сохранилась.';
            this.loadingCreative = false;
        },
        closeCreativeModal() {
            this.showCreativeModal = false;
            this.currentCreative = null;
            this.creativeError = null;
            this.loadingCreative = false;
        },
        isImageCreative(creative) {
            if (!creative || !creative.url) return false;
            const url = creative.url.toLowerCase();
            return /\.(png|jpe?g|webp|gif|bmp|svg)(\?.*)?$/i.test(url);
        },
        isVideoCreative(creative) {
            if (!creative || !creative.url) return false;
            const url = creative.url.toLowerCase();
            return /\.(mp4|webm|ogg|mov|avi)(\?.*)?$/i.test(url);
        },
        getCreativeUrl(creative) {
            if (!creative) return '';
            return creative.url || creative.preview || '';
        },
        handleCreativeImageError() {
            this.creativeError = 'Не удалось загрузить изображение';
        },
        async openFrameMapModal(gid) {
            this.showFrameMapModal = true;
            this.loadingFrame = true;
            this.frameError = null;
            this.currentFrame = null;

            try {
                const response = await axios.get(`/api/rtb-requests/frame/${gid}`);
                this.currentFrame = response.data;
            } catch (error) {
                if (error.response?.status === 404) {
                    this.frameError = 'Экран не найден';
                } else {
                    this.frameError = 'Не удалось загрузить данные экрана';
                }
            } finally {
                this.loadingFrame = false;
            }
        },
        closeFrameMapModal() {
            this.showFrameMapModal = false;
            this.currentFrame = null;
            this.frameError = null;
            this.loadingFrame = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.rtb-requests {
    padding: 20px;
    max-width: 1400px;
    margin: 0 auto;

    &__header {
        margin-bottom: 30px;
    }

    &__title {
        font-size: 24px;
        font-weight: 600;
        margin-bottom: 20px;
    }

    &__total {
        margin-bottom: 15px;

        .stat-card {
            width: 100%;
        }
    }

    &__stats {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 15px;
        margin-bottom: 30px;
    }

    &__filters {
        display: grid;
        grid-template-columns: repeat(4, minmax(180px, 1fr));
        gap: 15px;
        align-items: start;
        margin-bottom: 20px;
        padding: 15px;
        background: #f5f5f5;
        border-radius: 8px;

        &.is-admin {
            grid-template-columns: repeat(6, minmax(180px, 1fr));
        }

        > * {
            min-width: 0;
        }

        > .filter-group {
            min-width: 180px;
        }
    }

    &__table-container {
        overflow-x: auto;
        margin-bottom: 20px;
    }
}

.stat-card {
    padding: 15px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-left: 4px solid #ccc;

    &--total {
        border-left-color: #3498db;
    }

    &--completed {
        border-left-color: #27ae60;
    }

    &--bid_sent {
        border-left-color: #2980b9;
    }

    &--pending {
        border-left-color: #f39c12;
    }

    &--no_bid {
        border-left-color: #95a5a6;
    }

    &--failed {
        border-left-color: #e74c3c;
    }

    &__label {
        font-size: 12px;
        color: #666;
        margin-bottom: 5px;
    }

    &__value {
        font-size: 24px;
        font-weight: 600;
    }
}

.filter-datetime-stack {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .filter-group {
        min-width: 0;
    }
}

.filter-actions {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;

    .btn {
        width: 100%;
        box-sizing: border-box;
        justify-content: center;
        white-space: normal;
        text-align: center;
    }
}

.btn--with-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.filter-apply-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.4em;
    height: 1.4em;
    padding: 0 0.35em;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.95);
    color: #1a5fb4;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.filter-label {
    font-size: 12px;
    color: #666;
}

.filter-input {
    width: 100%;
    box-sizing: border-box;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
}

.rtb-table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    background: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    th.col-ids,
    td.col-ids {
        width: 32%;
    }

    th.col-time,
    td.col-time {
        width: 21%;
    }

    th.col-default,
    td.col-default {
        width: 12%;
    }

    th {
        padding: 12px;
        text-align: left;
        background: #f8f9fa;
        font-weight: 600;
        border-bottom: 2px solid #dee2e6;
    }

    td {
        padding: 12px;
        border-bottom: 2px solid #dee2e6;
    }

    tr:hover {
        background: #f8f9fa;
    }
}

.request-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
}

.ssp-badge {
    letter-spacing: 0.02em;
}

.org-name {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 2px;
}

.req-id,
.oid-value {
    font-size: 13px;
    color: #374151;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.gid-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;

    &--confirmed {
        background: #d1fae5;
        color: #065f46;
    }

    &--unconfirmed {
        background: #fee2e2;
        color: #991b1b;
    }

    &--neutral {
        background: #e5e7eb;
        color: #374151;
    }
}

.status-badge {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;

    &--pending {
        background: #fff3cd;
        color: #856404;
    }

    &--bid_sent {
        background: #cce5ff;
        color: #004085;
    }

    &--completed {
        background: #d4edda;
        color: #155724;
    }

    &--failed {
        background: #f8d7da;
        color: #721c24;
    }

    &--no_bid {
        background: #e2e3e5;
        color: #495057;
    }
}

.vertical-cell {
    padding: 12px;
}

.vertical-item {
    display: flex;
    flex-direction: column;
    margin-bottom: 8px;
    align-items: flex-start;

    &:last-child {
        margin-bottom: 0;
    }
}

.vertical-label {
    font-size: 11px;
    color: #666;
    margin-bottom: 2px;
    font-weight: 500;
}

.error-cell {
    text-align: center;
}

.screenshot-link {
    color: #3498db;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
}

.text-center {
    text-align: center;
}

.text-muted {
    color: #6c757d;
}

.screenshot-eye-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 18px;
    padding: 4px 8px;
    transition: transform 0.2s;

    &:hover {
        transform: scale(1.2);
    }

    &:active {
        transform: scale(1.1);
    }
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.screenshot-modal {
    background: white;
    border-radius: 8px;
    max-width: 90vw;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e0e0e0;

    h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
    }
}

.modal-close {
    background: none;
    border: none;
    font-size: 28px;
    cursor: pointer;
    color: #666;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background 0.2s;

    &:hover {
        background: #f0f0f0;
    }
}

.screenshot-modal-body {
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: auto;
    max-height: calc(90vh - 80px);
}

.screenshot-image {
    max-width: 100%;
    max-height: calc(90vh - 120px);
    object-fit: contain;
    border-radius: 4px;
}

.screenshot-error {
    padding: 40px;
    text-align: center;
    color: #dc3545;
    font-size: 16px;
}

.screenshot-loading {
    padding: 40px;
    text-align: center;
    color: #6c757d;
    font-size: 16px;
}

.loading {
    text-align: center;
    padding: 40px;
    color: #6c757d;
}

.error-icon {
    color: #e74c3c;
    font-size: 18px;
    cursor: help;
    display: inline-block;
    text-align: center;
}

.creative-info {
    display: inline-block;
}

.creative-link-row {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.creative-locale-badge {
    display: inline-flex;
    align-items: center;
    border: 1px solid #cbd5e1;
    border-radius: 999px;
    padding: 2px 10px;
    font-size: 11px;
    font-weight: 600;
    color: #334155;
    background: #f8fafc;
    line-height: 1.4;
}

.creative-badge {
    color: #27ae60;
    font-size: 14px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: help;
    font-weight: 500;
}

.link-button {
    color: #3498db;
    text-decoration: none;
    font-size: 14px;
    display: inline-block;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
        color: #2980b9;
        text-decoration: underline;
    }
}

.frame-gid-link {
    color: inherit;
    text-decoration: none;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
}

.load-more-container {
    text-align: center;
    padding: 20px;
    margin-top: 20px;
}

.loading-more {
    text-align: center;
    padding: 20px;
    color: #6c757d;
    margin-top: 20px;
}

.no-more {
    text-align: center;
    padding: 20px;
    color: #6c757d;
    font-style: italic;
    margin-top: 20px;
}

.creative-modal {
    background: white;
    border-radius: 8px;
    max-width: 90vw;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.creative-modal-body {
    padding: 20px;
    overflow: auto;
    max-height: calc(90vh - 80px);
}

.creative-loading {
    padding: 40px;
    text-align: center;
    color: #6c757d;
    font-size: 16px;
}

.creative-error {
    padding: 40px;
    text-align: center;
    color: #dc3545;
    font-size: 16px;
}

.creative-preview {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
}

.creative-name {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    text-align: center;
}

.creative-image-container {
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 100%;
}

.creative-image {
    max-width: 100%;
    max-height: calc(90vh - 200px);
    object-fit: contain;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.creative-video-container {
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 100%;
}

.creative-video {
    max-width: 100%;
    max-height: calc(90vh - 200px);
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.creative-info-text {
    padding: 20px;
    text-align: center;
    color: #666;

    p {
        margin: 10px 0;
    }

    a {
        color: #3498db;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }
}

.error-modal-list {
    margin-bottom: 12px;
    text-align: left;
}

.error-modal-title {
    font-weight: 600;
    margin-bottom: 6px;
}

.error-reason {
    margin-left: 12px;
    color: #333;
    white-space: pre-wrap;
    margin-bottom: 4px;
    font-size: 14px;
}

.frame-map-modal {
    background: white;
    border-radius: 8px;
    max-width: 90vw;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.frame-map-modal-body {
    padding: 20px;
    overflow: auto;
    max-height: calc(90vh - 80px);
}

.frame-loading {
    padding: 40px;
    text-align: center;
    color: #6c757d;
    font-size: 16px;
}

.frame-error {
    padding: 40px;
    text-align: center;
    color: #dc3545;
    font-size: 16px;
}

.frame-map-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.frame-info {
    padding: 15px;
    background: #f8f9fa;
    border-radius: 4px;

    p {
        margin: 8px 0;
        font-size: 14px;
        color: #333;

        strong {
            color: #555;
        }
    }
}

.time-item {
    display: flex;
    width: 90%;
    justify-content: space-between;
    align-items: center;
}

.frame-no-coords {
    padding: 40px;
    text-align: center;
    color: #6c757d;
    font-size: 16px;
}

.charged-price {
    color: #2563eb;
    font-weight: 500;
}

.vertical-item .frame-address,
.vertical-item .frame-creative-size {
    display: block;
    font-size: 12px;
    color: #9ca3af;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.vertical-item .frame-creative-size {
    color: #6b7280;
    font-weight: 500;
}

.col-ids__value {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
}

/* Filter input row & search button */
.filter-info {
    display: inline-block;
    margin-left: 12px;
    font-size: 14px;
    color: #374151;
    align-self: center;
}

.error-rejection-modal {
    max-width: 720px;
    width: min(96vw, 720px);
}

.error-rejection-count {
    font-weight: 400;
    color: #6b7280;
    font-size: 0.9em;
}

.error-rejection-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 65vh;
    overflow-y: auto;
}

.error-rejection-item {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fafafa;
}

.error-rejection-item__main {
    display: flex;
    gap: 12px;
    flex: 1;
    min-width: 0;
}

.error-rejection-item__num {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #fee2e2;
    color: #b91c1c;
    font-size: 12px;
    font-weight: 600;
}

.error-rejection-item__content {
    flex: 1;
    min-width: 0;
}

.error-rejection-code {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
}

.error-rejection-code__badge {
    font-family: ui-monospace, monospace;
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 4px;
    background: #e5e7eb;
    color: #374151;
}

.error-rejection-code__label {
    font-size: 12px;
    color: #6b7280;
}

.error-rejection-text {
    margin: 0;
    font-size: 14px;
    line-height: 1.45;
    color: #111827;
    word-break: break-word;
}

.error-rejection-actions {
    flex-shrink: 0;
    display: flex;
    align-items: flex-start;
}

.error-rejection-link {
    white-space: nowrap;
    font-size: 13px;
}

.error-rejection-empty {
    margin: 0;
    color: #6b7280;
    text-align: center;
    padding: 24px;
}
</style>
